import React from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
  Chip,
  Stack,
  IconButton,
} from '@mui/material';
import {
  Delete,
  Assignment,
  CheckCircle,
  HourglassEmpty,
  Archive,
  Edit,
} from '@mui/icons-material';

type InspectionStatus = 'DRAFT' | 'IN_PROGRESS' | 'COMPLETED' | 'ARCHIVED';

interface Inspection {
  id: string;
  title: string;
  inspectionStatus: InspectionStatus;
  inspectionDate?: string | null;
  propertyAddress?: string | null;
  defects?: {id: string}[];
}

interface InspectionListProps {
  inspections: Inspection[];
  selectedId?: string;
  onSelect: (inspection: Inspection) => void;
  onDelete?: (inspectionId: string) => void;
}

const statusConfig: Record<
  InspectionStatus,
  {icon: React.ReactNode; color: 'default' | 'primary' | 'success' | 'warning'}
> = {
  DRAFT: {icon: <Edit fontSize="small" />, color: 'default'},
  IN_PROGRESS: {icon: <HourglassEmpty fontSize="small" />, color: 'warning'},
  COMPLETED: {icon: <CheckCircle fontSize="small" />, color: 'success'},
  ARCHIVED: {icon: <Archive fontSize="small" />, color: 'default'},
};

export function InspectionList({
  inspections,
  selectedId,
  onSelect,
  onDelete,
}: InspectionListProps) {
  if (inspections.length === 0) {
    return (
      <Box textAlign="center" py={4}>
        <Assignment sx={{fontSize: 60, color: 'text.secondary', mb: 2}} />
        <Typography variant="h6" gutterBottom>
          No inspections yet
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Create your first inspection to get started
        </Typography>
      </Box>
    );
  }

  return (
    <List sx={{p: 0}}>
      {inspections.map((inspection) => {
        const status =
          statusConfig[inspection.inspectionStatus] || statusConfig.DRAFT;
        const defectCount = inspection.defects?.length || 0;

        return (
          <ListItem
            key={inspection.id}
            disablePadding
            sx={{
              mb: 1,
              border: 1,
              borderColor:
                selectedId === inspection.id ? 'primary.main' : 'divider',
              borderRadius: 1,
              bgcolor:
                selectedId === inspection.id
                  ? 'primary.lighter'
                  : 'transparent',
            }}
            secondaryAction={
              onDelete && (
                <IconButton
                  edge="end"
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(inspection.id);
                  }}
                  color="error"
                >
                  <Delete fontSize="small" />
                </IconButton>
              )
            }
          >
            <ListItemButton onClick={() => onSelect(inspection)}>
              <ListItemText
                primary={
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Typography variant="subtitle2" fontWeight={600}>
                      {inspection.title}
                    </Typography>
                    <Chip
                      icon={status.icon as React.ReactElement}
                      label={inspection.inspectionStatus.replace('_', ' ')}
                      size="small"
                      color={status.color}
                      sx={{ml: 1}}
                    />
                  </Stack>
                }
                secondary={
                  <Stack spacing={0.5} mt={0.5}>
                    {inspection.propertyAddress && (
                      <Typography variant="caption" color="text.secondary">
                        {inspection.propertyAddress}
                      </Typography>
                    )}
                    <Stack direction="row" spacing={1} alignItems="center">
                      {inspection.inspectionDate && (
                        <Typography variant="caption" color="text.secondary">
                          {new Date(
                            inspection.inspectionDate
                          ).toLocaleDateString()}
                        </Typography>
                      )}
                      <Chip
                        label={`${defectCount} defects`}
                        size="small"
                        variant="outlined"
                        sx={{height: 18, fontSize: '0.65rem'}}
                      />
                    </Stack>
                  </Stack>
                }
              />
            </ListItemButton>
          </ListItem>
        );
      })}
    </List>
  );
}
